<template>
  <div class="screen-cotainer">
    <v-layout column>
      <v-flex class="title-screen" xs12>
        <span>Curated invitations</span>
      </v-flex>
      <div v-if="!$apollo.loading" class="attendee_table">
        <div style="margin-bottom: 1rem; display: flex; justify-content: space-between">
          <main-button @click="goToCreateInvitee">
            <span class="add-symbol">+</span>Add invitee
          </main-button>
          <div>
            <main-button outline @click="goToImportInvitees">
              <img
                style="height: 2rem; margin-right: 0.5rem"
                src="/assets/import.svg"
                alt="Import"
              />Import
            </main-button>
            <a
              download
              :href="`${serverURL}/files/events/${eventId}/export/curated-invitees/xlsx/${exportFields}/${token}`"
            >
              <main-button outline>
                <img
                  style="height: 2rem; margin-right: 0.5rem"
                  src="/assets/export.svg"
                  alt="Import"
                />Export
              </main-button>
            </a>
          </div>
        </div>
        <table-filters
          v-if="!showAlternateHeader"
          :visible-fields.sync="visibleFields"
          :all-fields="allFields"
          :initial-filters="{ quickSearch, ...filters }"
          :attendee-fields="attendeeFields"
          :categories="categories"
          :extra-modules="extraModules"
          :event-id="eventId"
          @quickSearch="handleQuickSearch"
          @filters="handleFilters"
        />
        <selected-attendees-header
          v-else
          :selected="selected"
          :show-print="false"
          :show-send-email="false"
          :show-accept="true"
          :event-id="eventId"
          @clear="selected = []"
          @delete="showDeleteDialog = true"
          @accept="acceptInvitees"
        />
        <div
          v-if="attendees.length === 0"
          class="no-attendees-message-container border__left border__right"
        >
          <span class="no-attendees-message">No matching records found.</span>
        </div>
        <table-list
          v-else
          :event-id="eventId"
          :organization-slug="organizationSlug"
          :selected.sync="selected"
          :attendees="attendees"
          :visible-fields="visibleFields"
          :attendee-fields="attendeeFields"
          :pagination="pagination"
          @click="goTo(routes.organizer.editAccreditation, { accreditationId: $event })"
          @sort="handleSort($event)"
          @attendeeDeleted="fetchAttendees(pagination, filters, quickSearch)"
        />
        <delete-dialog
          :content="`Are you sure you want to delete selected accreditations? This operation cannot be undone.`"
          :on-delete="deleteSelected"
          :show.sync="showDeleteDialog"
          title="Delete accreditations"
        />
        <table-pagination
          :pagination="pagination"
          :total-attendees="totalAttendeeCount"
          @change="handlePagination($event)"
          @changePerPage="handlePagination($event)"
        />
      </div>
    </v-layout>
  </div>
</template>

<script>
import { difference } from 'lodash'

import TableFilters from './attendee-table/filters.vue'
import TableList from './attendee-table/list.vue'
import TablePagination from './attendee-table/pagination.vue'

import { objectToArray, getVisibleFields, setVisibleFields, serverURL } from '../../utils'
import { GET_EVENT_DETAILS, GET_EVENT_ACCREDITATIONS_ONLY } from '../../graphql/event.graphql'
import { listOrderComparator } from './attendee-fields'
import SelectedAttendeesHeader from '@/components/accreditations/attendee-table/selected-attendees-header.vue'
import DeleteDialog from '@/components/shared/dialogs/delete-dialog.vue'
import { DELETE_ATTENDEES } from '@/graphql/accreditation.graphql'
import { approveCuratedInvitees, getExportToken } from '@/http'

export default {
  name: 'curated-invitation',
  components: {
    DeleteDialog,
    SelectedAttendeesHeader,
    TableFilters,
    TableList,
    TablePagination,
  },
  props: {
    eventId: String,
    user: Object,
    organizationSlug: String,
  },
  data() {
    const { pagination, quickSearch, filters } = this.getInitialFromQueryParams()

    return {
      attendees: [],
      totalAttendeeCount: 0,
      categories: [],
      attendeeFields: [],
      eventAttendeeFields: {},
      serverURL,
      extraModules: {},
      selected: [],
      quickSearch,
      showDeleteDialog: false,
      pagination,
      url: '',
      filters,
      paginationLoading: false,
      visibleFields: [],
      allFields: [],
      exportFields: [],
      token: null,
    }
  },
  computed: {
    showAlternateHeader() {
      return this.selected && this.selected.length > 0
    },
  },
  async created() {
    const { data } = await getExportToken(this.eventId)
    this.token = data.token
    const localStorageVisibleFields = getVisibleFields(this.eventId)
    this.exportFields = JSON.stringify(
      localStorageVisibleFields.filter(field => !field.includes('/')),
    )
  },
  async mounted() {
    await this.fetchAttendees(this.pagination, this.filters, this.quickSearch)
  },
  apollo: {
    getEvent: {
      query: GET_EVENT_DETAILS,
      variables() {
        return {
          eventId: this.eventId,
          status: 'ALLOWED',
        }
      },
      manual: true,
      result({ data, loading }) {
        if (!loading) {
          const {
            getEvent: { extraModules, categories, attendeeFields, quickPrint },
          } = data
          this.categories = categories || []
          this.attendeeFields = attendeeFields
          this.eventAttendeeFields = objectToArray(attendeeFields, 'name').sort(listOrderComparator)
          this.extraModules = extraModules || {}
          this.quickPrint = quickPrint
          this.allFields = [...this.eventAttendeeFields.map(f => f.name)]
          if (this.extraModules.categoriesAndZones) {
            this.allFields = [...this.eventAttendeeFields.map(f => f.name), 'Category']
          }
          const localStorageFields = getVisibleFields(this.eventId)
          if (localStorageFields.length > 0) {
            if (difference(localStorageFields, this.allFields).length > 0) {
              // if there is a field in my local, and not in definition, it means that other user deleted it
              this.visibleFields = this.allFields
            } else {
              this.visibleFields = localStorageFields
            }
          } else {
            this.visibleFields = this.allFields
          }
          setVisibleFields(this.visibleFields, this.eventId)
          this.visibleFields.push('Status')
        }
      },
    },
  },
  methods: {
    async deleteSelected() {
      await this.$apollo.mutate({
        mutation: DELETE_ATTENDEES,
        variables: { attendeeIds: this.selected, eventId: this.eventId },
      })
      await this.fetchAttendees()
      this.selected = []
    },
    goToCreateInvitee() {
      return this.goTo(this.routes.organizer.createAccreditation, {
        eventId: this.eventId,
        screenTitle: 'New Invitee',
        hiddenCustomField: { curatedInvitee: true, gotEmailInv: false },
      })
    },
    goToImportInvitees() {
      return this.goTo(this.routes.organizer.import, {
        eventId: this.eventId,
        screenTitle: 'Import invitees',
        hiddenCustomField: { curatedInvitee: true, gotEmailInv: false },
      })
    },
    async acceptInvitees() {
      const variables = {
        attendeeIds: this.selected,
        eventId: this.eventId,
      }
      await approveCuratedInvitees(variables)
      await this.fetchAttendees()
      this.selected = []
    },
    async handleSort(sortBy) {
      const { per_page, page, sortBy: thisSortBy, descending } = this.pagination
      const pagination = {
        page,
        per_page,
        sortBy,
        descending: sortBy === thisSortBy ? !descending : false,
      }
      this.addPaginationToRoute(pagination)
      this.pagination = pagination

      await this.fetchAttendees(
        pagination,
        this.filters,
        typeof this.quickSearch === 'string' ? { quickSearch: this.quickSearch } : this.quickSearch,
      )
    },
    async handleFilters(filters) {
      this.addFiltersToRoute(filters)
      this.filters = filters

      await this.fetchAttendees(this.pagination, this.filters, this.quickSearch)
    },
    async handleQuickSearch(quickSearch) {
      this.pagination = {
        ...this.pagination,
        page: 1,
      }
      this.addFiltersToRoute(quickSearch)
      this.quickSearch = quickSearch

      await this.fetchAttendees(this.pagination, this.filters, this.quickSearch)
    },
    async handlePagination({ page, per_page }) {
      const { page: currentPage, per_page: currentPerPage } = this.pagination
      if (currentPage > page && currentPage === 1) return
      if (page > currentPage && !(this.totalAttendeeCount - currentPerPage * currentPage > 0))
        return

      this.pagination = {
        ...this.pagination,
        per_page: per_page === 'All' ? -1 : per_page,
        page: page,
      }

      this.addPaginationToRoute(this.pagination)

      await this.fetchAttendees(this.pagination, this.filters, this.quickSearch)
    },
    getInitialFromQueryParams() {
      const { per_page, page, sortBy, descending, quickSearch, ...fieldFilters } = this.$route.query

      return {
        pagination: {
          page: parseInt(page, 10) || 1,
          per_page: parseInt(per_page, 10) || 25,
          sortBy: sortBy,
          descending: descending === 'true' ? true : false,
        },
        quickSearch: quickSearch || '',
        filters: fieldFilters || {},
      }
    },
    addPaginationToRoute({ page, per_page, sortBy, descending }) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          per_page,
          page,
          sortBy,
          descending,
        },
      })
    },
    addFiltersToRoute(filters) {
      const allFilters = { ...this.$route.query, ...filters }
      const filtersWithValueArray = Object.entries(allFilters).filter(f => f[1]) // remove entries with no value
      // const filtersWithValue = Object.fromEntries(filtersWithValueArray)
      const filtersWithValue = filtersWithValueArray.reduce((acc, e) => {
        acc[e[0]] = e[1]

        return acc
      }, {})

      this.$router.replace({
        query: {
          ...filtersWithValue,
        },
      })
    },
    async fetchAttendees(pagination, filters, quickSearch) {
      this.paginationLoading = true
      const {
        data: { getEventAccreditations },
      } = await this.$apollo.query({
        query: GET_EVENT_ACCREDITATIONS_ONLY,
        variables: {
          eventId: this.eventId,
          quickSearch: (quickSearch && quickSearch.quickSearch) || '',
          visibleFields: this.visibleFields,
          pagination: pagination || this.pagination,
          filters: { ...(filters || this.filters || {}), curatedInvitee: true },
        },
      })
      if (!getEventAccreditations) {
        return
      }
      const { attendees, attendeeCount } = getEventAccreditations
      this.attendees = attendees.map(a => {
        return {
          ...a,
          status: !a.fields.gotEmailInv ? 'NEW' : a.status,
        }
      })
      this.totalAttendeeCount = parseInt(attendeeCount, 10)
      this.paginationLoading = false
    },
  },
}
</script>

<style scoped>
.no-attendees-message-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-attendees-message {
  color: #979797;
  padding: 20px;
}

.border__left {
  border-left: 2px solid #f5f5f5;
}

.border__right {
  border-right: 2px solid #f5f5f5;
}

ul {
  list-style-type: none;
}

span {
  font-weight: bold;
}

@media screen and (max-width: 800px) {
  .screen-cotainer {
    margin-top: 1.5rem;
  }

  .title-screen {
    display: none;
  }
}

.title-screen {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
</style>
